import React from 'react';

/**
 * The section heading standard — titles a section inside a page: bold label
 * in the app's strong header purple (primary-700 — NOT the light eyebrow
 * tint), an optional muted count/context note, a hairline filling the
 * remaining width, and an optional right-aligned slot.
 *
 * Ratified 2026-08-15 after the NGR Reports page shipped washed-out
 * purple-400 headers: in-page section headers use the standard header color,
 * matching page and panel headings elsewhere.
 */
interface SectionHeadingProps {
  /** The uppercase eyebrow label. */
  children: React.ReactNode;
  /** Muted note beside the label — usually a count ("6 maintained by …"). */
  note?: React.ReactNode;
  /** Right-aligned slot after the hairline (hints, small actions). */
  right?: React.ReactNode;
  /** id for aria-labelledby wiring. */
  id?: string;
  className?: string;
}

export const SECTION_HEADING_CLASSES =
  'text-sm font-bold uppercase tracking-wide text-primary-700 dark:text-primary-dark-400';

const SectionHeading: React.FC<SectionHeadingProps> = ({ children, note, right, id, className }) => (
  <div className={`flex items-center gap-3 ${className || ''}`}>
    <h2 id={id} className={SECTION_HEADING_CLASSES}>
      {children}
    </h2>
    {note && (
      <span className="text-xs font-semibold text-text-tertiary dark:text-text-dark-tertiary">{note}</span>
    )}
    <div className="h-px flex-1 bg-border-light dark:bg-border-dark-hairline" />
    {right && (
      <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">{right}</span>
    )}
  </div>
);

export default SectionHeading;
